// DS3.2
import React from 'react';
import InputErrorMessage from '../../lego/InputErrorMessage';
import InputHint from '../../lego/InputHint';
import InputLabelSubText from '../../lego/InputLabelSubText';
import InputLabelText from '../../lego/InputLabelText';
import InputSuccessMessage from '../../lego/InputSuccessMessage';
import FormElement, { FormElementProps } from '../../layouts/FormElement';
import LimitedTextAreaInputField from '../../lego/LimitedTextAreaInputField';
import TextAreaInputField, {
  TextAreaInputFieldProps,
} from '../../lego/TextAreaInputField';

export interface Props
  extends TextAreaInputFieldProps,
    Pick<FormElementProps, 'isLabelHidden' | 'noBottomSpacing'> {
  /** Required for a11y reasons. We need to be able to link autocomplete menus and labels to the input field. */
  id: string;

  /**
   * Displays above the input field.
   *
   * Required for a11y reasons. If design won't work a label into the spec, consider passing through `isLabelHidden`.
   */
  label: string;

  /** Displays below the `label` in smaller font */
  subLabel?: string;

  /**
   * Displays below the input field.
   *
   * Depending on the value of `validationResult`, it'll be wrapped in...
   *
   * `error`: `<InputErrorMessage />`
   *
   * `success`: `<InputSuccessMessage />`
   *
   * `undefined`: `<InputHint />`
   *
   */
  details?: React.ReactNode;

  /** A class name to be used in the FormElement component that wraps the input */
  wrapperClassName?: string;

  value?: string;
  defaultValue?: string;

  /** Whether to show the character counter when maxLength is set. Defaults to true. */
  showCounter?: boolean;

  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
}

/**
 * A multiline text input field with a label and some details.
 *
 * For use on our member subscription tier creation form.
 */
const TextArea = React.forwardRef<HTMLTextAreaElement, Props>(
  (
    {
      id,
      label,
      subLabel,
      isRequired,
      isLabelHidden,
      maxLength,
      details,
      validationResult,
      noBottomSpacing,
      wrapperClassName,
      showCounter,
      ...rest
    },
    ref
  ) => {
    let wrappedDetails: React.ReactNode = null;
    const errorMessageId = `${id}-error`;
    const hintId = `${id}-hint`;

    switch (validationResult) {
      case 'success':
        wrappedDetails = <InputSuccessMessage>{details}</InputSuccessMessage>;
        break;
      case 'error':
        wrappedDetails = (
          <InputErrorMessage id={errorMessageId}>{details}</InputErrorMessage>
        );
        break;
      default:
        wrappedDetails = details ? (
          <InputHint id={hintId}>{details}</InputHint>
        ) : undefined;
    }

    const commonInputProps = {
      id,
      isRequired,
      validationResult,
      ref,
      'aria-errormessage':
        validationResult === 'error' ? errorMessageId : undefined,
      'aria-describedby':
        validationResult === undefined && details ? hintId : undefined,
    };

    return (
      <FormElement
        inputId={id}
        isLabelHidden={isLabelHidden}
        noBottomSpacing={noBottomSpacing}
        label={<InputLabelText isRequired={isRequired}>{label}</InputLabelText>}
        subLabel={subLabel && <InputLabelSubText>{subLabel}</InputLabelSubText>}
        details={wrappedDetails}
        className={wrapperClassName}
      >
        {maxLength === undefined ? (
          <TextAreaInputField {...commonInputProps} {...rest} />
        ) : (
          <LimitedTextAreaInputField
            {...commonInputProps}
            maxLength={maxLength}
            showCounter={showCounter}
            {...rest}
          />
        )}
      </FormElement>
    );
  }
);
TextArea.displayName = 'TextArea';

export default TextArea;
